// ===================================================
// GAM Command Center — מחברת (Pinkas) — workspace state
// Pure helpers for the unified notebook screen: the open-notes tray, the
// per-user persisted layout, local drafts and plain-text search.
// Spec: docs/specs/pinkas-unified-workspace-20260924.md
//
// Everything that touches localStorage goes through safeGet/safeSet: storage
// can be missing, full or blocked (private window), and the notebook must keep
// working — it just stops remembering.
// ===================================================
import type { JSONContent } from "@tiptap/core";

// ---- tray ------------------------------------------------------------------
// The tray is an ordered list of note ids the user chose to keep open. Order is
// the user's; nothing here sorts by activity, so a background save never moves
// a tab.

export function trayAdd(tray: string[], id: string): string[] {
  return tray.includes(id) ? tray : [...tray, id];
}

/** Closes a tab. Returns the new tray and which note to show if the closed tab was the active one. */
export function trayClose(
  tray: string[],
  id: string,
  activeId: string | null,
): { tray: string[]; activeId: string | null } {
  const idx = tray.indexOf(id);
  if (idx === -1) return { tray, activeId };
  const next = tray.filter((x) => x !== id);
  if (activeId !== id) return { tray: next, activeId };
  // Neighbour on the same side first (the tab that slides into place), then the previous one.
  const neighbour = next[idx] ?? next[idx - 1] ?? null;
  return { tray: next, activeId: neighbour };
}

export function trayMove(tray: string[], fromId: string, toId: string): string[] {
  const from = tray.indexOf(fromId);
  const to = tray.indexOf(toId);
  if (from === -1 || to === -1 || from === to) return tray;
  const next = [...tray];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Drops ids that no longer exist (deleted, or no longer visible to this user). */
export function prunePersisted(state: WorkspaceState, existing: ReadonlySet<string>): WorkspaceState {
  const tray = state.tray.filter((id) => existing.has(id));
  const activeId = state.activeId && existing.has(state.activeId) ? state.activeId : null;
  const positions: WorkspaceState["positions"] = {};
  for (const [id, pos] of Object.entries(state.positions)) if (existing.has(id)) positions[id] = pos;
  if (
    tray.length === state.tray.length &&
    activeId === state.activeId &&
    Object.keys(positions).length === Object.keys(state.positions).length
  ) {
    return state;
  }
  return { ...state, tray, activeId, positions };
}

// ---- persisted workspace ---------------------------------------------------

export type ListView = "active" | "pinned" | "archived";
export type AssigneeFilter = "all" | "gal" | "claude";
export type ListSort = "updated" | "priority" | "status";

export interface NotePosition {
  /** Scroll offset of the editor pane. */
  top: number;
  /** ProseMirror selection. */
  from: number;
  to: number;
}

export interface WorkspaceState {
  tray: string[];
  activeId: string | null;
  listOpen: boolean;
  detailsOpen: boolean;
  search: string;
  view: ListView;
  filter: AssigneeFilter;
  sort: ListSort;
  listScrollTop: number;
  positions: Record<string, NotePosition>;
}

export const DEFAULT_WORKSPACE: WorkspaceState = {
  tray: [],
  activeId: null,
  listOpen: true,
  detailsOpen: false,
  search: "",
  view: "active",
  filter: "all",
  sort: "updated",
  listScrollTop: 0,
  positions: {},
};

const MAX_POSITIONS = 60;

export function workspaceKey(tenant: string, userId: string): string {
  return `cc_pinkas_ws:v1:${tenant}:${userId}`;
}

export function draftKey(tenant: string, userId: string, noteId: string): string {
  return `cc_pinkas_draft:v1:${tenant}:${userId}:${noteId}`;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // storage full or blocked — the screen keeps working without memory
  }
}

const VIEWS: ListView[] = ["active", "pinned", "archived"];
const FILTERS: AssigneeFilter[] = ["all", "gal", "claude"];
const SORTS: ListSort[] = ["updated", "priority", "status"];

/** Reads the saved layout, accepting only well-formed fields — an old or hand-edited value never crashes the screen. */
export function loadWorkspace(key: string): WorkspaceState {
  const raw = safeGet(key);
  if (!raw) return DEFAULT_WORKSPACE;
  try {
    const v = JSON.parse(raw) as Partial<WorkspaceState>;
    const ids = (a: unknown) => (Array.isArray(a) ? [...new Set(a.filter((x): x is string => typeof x === "string"))] : []);
    const positions: Record<string, NotePosition> = {};
    if (v.positions && typeof v.positions === "object") {
      for (const [id, p] of Object.entries(v.positions)) {
        if (p && typeof p.top === "number" && typeof p.from === "number" && typeof p.to === "number") positions[id] = p;
      }
    }
    return {
      tray: ids(v.tray),
      activeId: typeof v.activeId === "string" ? v.activeId : null,
      listOpen: typeof v.listOpen === "boolean" ? v.listOpen : true,
      detailsOpen: v.detailsOpen === true,
      search: typeof v.search === "string" ? v.search : "",
      view: VIEWS.includes(v.view as ListView) ? (v.view as ListView) : "active",
      filter: FILTERS.includes(v.filter as AssigneeFilter) ? (v.filter as AssigneeFilter) : "all",
      sort: SORTS.includes(v.sort as ListSort) ? (v.sort as ListSort) : "updated",
      listScrollTop: typeof v.listScrollTop === "number" ? v.listScrollTop : 0,
      positions,
    };
  } catch {
    return DEFAULT_WORKSPACE;
  }
}

export function saveWorkspace(key: string, state: WorkspaceState): void {
  // Keep only the most recent positions so the entry cannot grow without bound.
  const entries = Object.entries(state.positions);
  const positions = entries.length > MAX_POSITIONS ? Object.fromEntries(entries.slice(-MAX_POSITIONS)) : state.positions;
  safeSet(key, JSON.stringify({ ...state, positions }));
}

// ---- drafts ----------------------------------------------------------------
// A draft is written on every keystroke, before any network call. It is the
// recovery path for refresh, crash and closed tabs; it is removed only after
// the server confirmed the same content.

export interface NoteDraft {
  title: string;
  body: JSONContent | null;
  /** Server updated_at the draft was based on — tells a stale draft from a fresh one. */
  baseUpdatedAt: string;
  /** Monotonic write stamp, so a late save cannot erase a newer draft. */
  rev: number;
}

export function readDraft(key: string): NoteDraft | null {
  const raw = safeGet(key);
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as NoteDraft;
    if (typeof d.title !== "string" || typeof d.baseUpdatedAt !== "string" || typeof d.rev !== "number") return null;
    return { title: d.title, body: d.body ?? null, baseUpdatedAt: d.baseUpdatedAt, rev: d.rev };
  } catch {
    return null;
  }
}

export function writeDraft(key: string, draft: NoteDraft): void {
  safeSet(key, JSON.stringify(draft));
}

/** Removes the draft only if nothing newer was written since `rev`. */
export function clearDraftIfRev(key: string, rev: number): void {
  const current = readDraft(key);
  if (current && current.rev === rev) safeSet(key, null);
}

export function sameContent(a: { title: string; body: unknown }, b: { title: string; body: unknown }): boolean {
  return a.title === b.title && JSON.stringify(a.body ?? null) === JSON.stringify(b.body ?? null);
}

/**
 * True when `next` is `base` after the editor's own normalization only: the
 * editor adds default node attributes (e.g. `textAlign`) when it loads a doc
 * and reports that as a change. Every node, text, mark and attribute already
 * in `base` must match; only attributes absent from `base` are ignored.
 */
export function isEditorNormalization(base: unknown, next: unknown): boolean {
  if (base === next) return true;
  if (base == null || next == null) return base == null && next == null;
  if (Array.isArray(base) || Array.isArray(next)) {
    if (!Array.isArray(base) || !Array.isArray(next) || base.length !== next.length) return false;
    return base.every((b, i) => isEditorNormalization(b, next[i]));
  }
  if (typeof base !== "object" || typeof next !== "object") return false;
  const b = base as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  for (const k of new Set([...Object.keys(b), ...Object.keys(n)])) {
    if (k === "attrs") {
      const ba = (b.attrs ?? {}) as Record<string, unknown>;
      const na = (n.attrs ?? {}) as Record<string, unknown>;
      if (typeof ba !== "object" || typeof na !== "object") return false;
      for (const ak of Object.keys(ba)) if (JSON.stringify(ba[ak]) !== JSON.stringify(na[ak])) return false;
      continue;
    }
    if (!isEditorNormalization(b[k], n[k])) return false;
  }
  return true;
}

export type DraftResolution =
  | { kind: "none" }
  | { kind: "discard" } // identical to the server — just drop it
  | { kind: "restore"; draft: NoteDraft } // unsaved local work on top of the current server version
  | { kind: "conflict"; draft: NoteDraft }; // unsaved local work AND the server moved on since

export function resolveDraft(
  draft: NoteDraft | null,
  server: { title: string; body: unknown; updatedAt: string },
): DraftResolution {
  if (!draft) return { kind: "none" };
  if (sameContent(draft, server)) return { kind: "discard" };
  if (draft.baseUpdatedAt && server.updatedAt && draft.baseUpdatedAt !== server.updatedAt) {
    return { kind: "conflict", draft };
  }
  return { kind: "restore", draft };
}

// ---- titles & text ---------------------------------------------------------

export const UNTITLED = "פתק ללא כותרת";

/** Flattens TipTap JSON to plain text (blocks separated by newlines). */
export function bodyToText(node: unknown): string {
  if (!node || typeof node !== "object") return "";
  const n = node as { type?: string; text?: string; content?: unknown[] };
  if (typeof n.text === "string") return n.text;
  if (!Array.isArray(n.content)) return "";
  const parts = n.content.map(bodyToText);
  const block = n.type === "doc" || n.type === "bulletList" || n.type === "orderedList" || n.type === "taskList";
  return parts.join(block ? "\n" : n.type === "paragraph" || n.type === "heading" ? "" : "\n");
}

/** A blank title never blocks writing: fall back to the first body line, then to a fixed temporary title. */
export function effectiveTitle(title: string, body: unknown): string {
  const t = title.trim();
  if (t) return t;
  const firstLine = bodyToText(body).split("\n").map((l) => l.trim()).find(Boolean);
  return firstLine ? firstLine.slice(0, 80) : UNTITLED;
}

export function snippet(body: unknown, max = 90): string {
  const text = bodyToText(body).replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// Hebrew niqqud / cantillation are dropped so a pointed and an unpointed word match.
const MARKS = /[֑-ׇ]/g;
export function normalizeForSearch(s: string): string {
  return s.toLowerCase().replace(MARKS, "").replace(/\s+/g, " ").trim();
}

/** Every whitespace-separated term must appear in the title or the body text. */
export function matchesSearch(item: { text: string; body: unknown; tags?: string[] | null }, query: string): boolean {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const hay = normalizeForSearch(`${item.text}\n${bodyToText(item.body)}\n${(item.tags ?? []).map((t) => `#${t}`).join(" ")}`);
  return q.split(" ").every((term) => hay.includes(term));
}
